import React, { useRef, useState } from "react";
import { X, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { apiService } from "../../services/api";
import type { BikeCsvImportResult } from "../../types/bike";

interface BikeCsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export const BikeCsvImportModal: React.FC<BikeCsvImportModalProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<BikeCsvImportResult | null>(null);

  const resetState = () => {
    setSelectedFile(null);
    setLoading(false);
    setError("");
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError("");
    setResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError("Please select a CSV file");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const importResult = await apiService.importBikesCsv(selectedFile);
      setResult(importResult);
      if (importResult.created > 0 || importResult.updated > 0) {
        onImported();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="bike-csv-import-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={handleClose}
          aria-hidden="true"
        />

        <span
          className="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="relative inline-block align-bottom w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:align-middle">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3
              id="bike-csv-import-title"
              className="text-lg font-medium text-gray-900"
            >
              Import Vehicles from CSV
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4 px-6 py-4">
            <p className="text-sm text-gray-600">
              Upload a CSV with columns <code className="text-xs">bike_sn</code>
              , <code className="text-xs">gps_device_id</code>,{" "}
              <code className="text-xs">category</code>, and{" "}
              <code className="text-xs">main_city</code>. Existing vehicles are
              updated; new ones are created.
            </p>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-500">
                  {selectedFile.name}
                </p>
              )}
            </div>

            {error && (
              <div className="flex items-start rounded-md bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {result && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm">
                <div className="mb-3 flex items-center text-green-700">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Import completed
                </div>
                <dl className="grid grid-cols-2 gap-2">
                  <div>
                    <dt className="text-gray-500">Total rows</dt>
                    <dd className="font-medium text-gray-900">
                      {result.totalRows}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Created</dt>
                    <dd className="font-medium text-gray-900">
                      {result.created}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Updated</dt>
                    <dd className="font-medium text-gray-900">
                      {result.updated}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Skipped</dt>
                    <dd className="font-medium text-gray-900">
                      {result.skipped}
                    </dd>
                  </div>
                </dl>

                {result.errors.length > 0 && (
                  <div className="mt-4 max-h-40 overflow-y-auto">
                    <p className="mb-2 font-medium text-gray-700">Errors</p>
                    <ul className="space-y-1 text-red-700">
                      {result.errors.map((entry, index) => (
                        <li key={`${entry.row}-${index}`}>
                          Row {entry.row}
                          {entry.gps_device_id
                            ? ` (${entry.gps_device_id})`
                            : ""}
                          : {entry.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={loading || !selectedFile}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="mr-2 h-4 w-4" />
              {loading ? "Importing..." : "Import CSV"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
